import { useEffect, useState } from 'react';
import { X, FileText, FileType, Loader2 } from 'lucide-react';
import type { UploadedAttachment } from '../lib/uploads';

export interface PendingAttachment {
  tempId: string;
  filename: string;
  size: number;
  status: 'uploading' | 'done' | 'error';
  progress?: number;
  error?: string;
  attachment?: UploadedAttachment;
  previewUrl?: string;
}

function ChipIcon({ a }: { a: PendingAttachment }) {
  if (a.previewUrl) {
    return (
      <img
        src={a.previewUrl}
        alt=""
        style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  const kind = a.attachment?.kind;
  const Icon = kind === 'pdf' ? FileType : FileText;
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 6,
      background: 'var(--gold-dim)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={14} style={{ color: 'var(--gold)' }} />
    </div>
  );
}

export function AttachmentChips({
  attachments,
  onRemove,
}: {
  attachments: PendingAttachment[];
  onRemove: (tempId: string) => void;
}) {
  if (attachments.length === 0) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 6,
      padding: '6px 4px 8px',
    }}>
      {attachments.map((a) => (
        <div
          key={a.tempId}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--card)',
            border: `1px solid ${a.status === 'error' ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
            borderRadius: 12,
            padding: '4px 8px 4px 4px',
            maxWidth: 220,
          }}
        >
          <ChipIcon a={a} />
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span style={{
              fontSize: 11, color: 'var(--text)', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 140,
            }}>{a.filename}</span>
            <span style={{ fontSize: 10, color: a.status === 'error' ? '#EF4444' : 'var(--muted)' }}>
              {a.status === 'uploading' && 'Analyzing…'}
              {a.status === 'done' && (a.attachment?.ai_summary?.slice(0, 40) || `${Math.round(a.size / 1024)}KB`)}
              {a.status === 'error' && (a.error || 'Failed')}
            </span>
          </div>
          {a.status === 'uploading' ? (
            <Loader2 size={14} style={{ color: 'var(--gold)', animation: 'spin 1s linear infinite' }} />
          ) : (
            <button
              type="button"
              aria-label={`Remove ${a.filename}`}
              onClick={() => onRemove(a.tempId)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--muted)', padding: 2, display: 'flex',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Renders saved attachments inside a chat message bubble.
export interface MsgAttachment {
  id: string;
  filename: string;
  mime: string;
  kind?: string;
  ai_summary?: string | null;
}

export function MessageAttachments({ items }: { items: MsgAttachment[] }) {
  const [previews, setPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const imgs = items.filter((i) => (i.mime || '').startsWith('image/'));
    Promise.all(imgs.map(async (i) => {
      try {
        const { fetchAttachmentBlobUrl } = await import('../lib/uploads');
        const url = await fetchAttachmentBlobUrl(i.id);
        if (!cancelled) setPreviews((p) => ({ ...p, [i.id]: url }));
      } catch { /* ignore */ }
    }));
    return () => {
      cancelled = true;
      Object.values(previews).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => i.id).join(',')]);

  if (items.length === 0) return null;

  const openOriginal = async (id: string) => {
    try {
      const { fetchAttachmentBlobUrl } = await import('../lib/uploads');
      const url = await fetchAttachmentBlobUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Could not open file', e);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
      {items.map((it) => {
        const isImage = (it.mime || '').startsWith('image/');
        const preview = previews[it.id];
        const Icon = it.kind === 'pdf' ? FileType : FileText;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => openOriginal(it.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.55)',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: 10,
              padding: 6,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
              maxWidth: 280,
            }}
          >
            {isImage && preview ? (
              <img src={preview} alt={it.filename}
                style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 44, height: 44, borderRadius: 6,
                background: 'rgba(255,255,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icon size={20} style={{ color: '#FFFFFF' }} />
              </div>
            )}
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <span style={{
                fontSize: 12, fontWeight: 600, color: '#FFFFFF',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: 220,
              }}>{it.filename}</span>
              {it.ai_summary && (
                <span style={{
                  fontSize: 11, color: 'rgba(255,255,255,0.85)',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>{it.ai_summary}</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
